import * as Sentry from '@sentry/react';
import { extractErrorString, getErrorStatus, isIndexable } from './api';

function apiErrorMessage(
    status: number,
    area: string,
    message: string | undefined,
): string {
    return message !== undefined
        ? `[${area}] API error (${status}): ${message}`
        : `[${area}] API error (${status}): <no message>`;
}

// Members that live in the `response` context or are folded into the message
// elsewhere, plus `type` which Sentry reserves inside context objects.
const CONTEXT_EXCLUDED = new Set(['type', 'Errors', 'Status', 'status']);

function buildContexts(
    error: unknown,
    status: number | undefined,
): Sentry.Contexts | undefined {
    const contexts: Sentry.Contexts = {};

    if (status !== undefined) {
        contexts.response = { status_code: status };
    }

    if (isIndexable(error, 'Detail')) {
        const problem: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(error)) {
            if (!CONTEXT_EXCLUDED.has(k)) {
                problem[k] = v;
            }
        }
        if (Object.keys(problem).length > 0) {
            contexts.problem = problem;
        }
    }

    return Object.keys(contexts).length > 0 ? contexts : undefined;
}

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMERIC_ID_RE = /\b\d{2,}\b/g;

function normalizeForFingerprint(value: string): string {
    return value.replace(GUID_RE, '<guid>').replace(NUMERIC_ID_RE, '<id>');
}

function isNetworkError(error: unknown): boolean {
    return error instanceof TypeError && error.message === 'Failed to fetch';
}

export function reportQueryError(error: unknown, fingerprintPrefix: string[]): void {
    const message = extractErrorString(error);
    const status = getErrorStatus(error);

    const area = fingerprintPrefix.join(':');

    const contexts = buildContexts(error, status);

    if (isNetworkError(error)) {
        Sentry.captureException(new Error('Network error'), {
            contexts,
            tags: {
                area,
            },
            extra: {
                raw: error,
            },
            fingerprint: ['react-query-network-error'],
        });
        return;
    }

    // A message means extractErrorString recognized a client-relevant problem
    // (a 4xx with a real Detail/Title).
    if (status !== undefined && message !== undefined) {
        const normalizedMessage = normalizeForFingerprint(message);
        Sentry.captureException(
            new Error(apiErrorMessage(status, area, normalizedMessage)),
            {
                contexts,
                tags: {
                    source: 'problem-details',
                    problemStatus: status.toString(),
                    area,
                },
                extra: { raw: error, rawMessage: message },
                fingerprint: [
                    ...fingerprintPrefix,
                    status.toString(),
                    normalizedMessage,
                    '{{ default }}',
                ],
            },
        );
        return;
    }

    // No extractable message. If it's a real Error, let Sentry do its normal
    // thing (stack trace, etc). Otherwise wrap it so we don't get the
    // "Object captured as exception with keys:" placeholder.
    if (error instanceof Error) {
        Sentry.captureException(error, {
            contexts,
            tags: {
                problemStatus: status?.toString(),
                area,
            },
            fingerprint: [
                ...fingerprintPrefix,
                status?.toString() ?? '<no-status>',
                '<no-message>',
                '{{ default }}',
            ],
        });
        return;
    }

    Sentry.captureException(
        new Error(
            status
                ? `[${area}] Server error (${status})`
                : `[${area}] Unhandled exception`,
        ),
        {
            contexts,
            tags: {
                problemStatus: status?.toString(),
                area,
            },
            extra: { raw: error },
            fingerprint: [
                ...fingerprintPrefix,
                status?.toString() ?? '<no-status>',
                '<no-message>',
                '{{ default }}',
            ],
        },
    );
}
